import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import {
  Package as PackageIcon, ClipboardList, User, Bookmark, LogOut, Settings, ChevronDown
} from 'lucide-react';

const CustomerMenu = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!user) return null;

  const menuItems = [
    { label: 'Mis pedidos', icon: ClipboardList, path: '/mi-cuenta/pedidos', testId: 'menu-orders' },
    { label: 'Diseños guardados', icon: Bookmark, path: '/mi-cuenta/disenos', testId: 'menu-designs' },
    { label: 'Mi perfil', icon: User, path: '/mi-cuenta/perfil', testId: 'menu-profile' },
  ];

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-border/60 hover:border-accent/40 bg-white hover:bg-muted/30 transition-all duration-200"
        data-testid="customer-menu-trigger"
      >
        {user.picture ? (
          <img src={user.picture} alt="" className="w-7 h-7 rounded-full" referrerPolicy="no-referrer" />
        ) : (
          <div className="w-7 h-7 rounded-full bg-accent/10 flex items-center justify-center text-xs font-bold text-accent">
            {user.name?.charAt(0) || user.email?.charAt(0) || '?'}
          </div>
        )}
        <span className="hidden sm:inline text-sm font-medium text-primary max-w-[120px] truncate">
          {user.name?.split(' ')[0] || 'Mi cuenta'}
        </span>
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-56 bg-white border border-border/60 rounded-xl shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
          {/* User info header */}
          <div className="px-4 py-3 border-b border-border/40 bg-muted/20">
            <p className="text-sm font-semibold text-primary truncate">{user.name || 'Cliente'}</p>
            <p className="text-xs text-muted-foreground truncate">{user.email}</p>
          </div>

          {/* Menu items */}
          <div className="py-1">
            {menuItems.map((item) => (
              <button
                key={item.path}
                onClick={() => { navigate(item.path); setOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-primary hover:bg-muted/40 transition-colors"
                data-testid={item.testId}
              >
                <item.icon className="w-4 h-4 text-muted-foreground" />
                {item.label}
              </button>
            ))}

            {user.is_admin && (
              <button
                onClick={() => { navigate('/admin'); setOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-accent font-medium hover:bg-accent/5 transition-colors"
                data-testid="menu-admin"
              >
                <Settings className="w-4 h-4" />
                Panel admin
              </button>
            )}
          </div>

          {/* Logout */}
          <div className="border-t border-border/40">
            <button
              onClick={() => { logout(); setOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
              data-testid="menu-logout"
            >
              <LogOut className="w-4 h-4" />
              Cerrar sesión
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerMenu;
